import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  Compass,
  GraduationCap,
  Sparkles,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { generateAiGrowthPlan, getCareerPlannerDirectionCatalog, getLatestAiGrowthPlanGeneration, recommendAiGrowthCareers } from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'

const EXPLORE_FORM_STORAGE_KEY = 'student_explore_direction_form'

function createInitialForm(searchParams) {
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(EXPLORE_FORM_STORAGE_KEY) || 'null')
    } catch {
      return null
    }
  })()

  return {
    major: searchParams.get('major') || searchParams.get('direction') || saved?.major || '',
    majorInterest: saved?.majorInterest || 'interested',
    preferredField: saved?.preferredField || '',
    grade: saved?.grade || 'junior',
    interest: saved?.interest || '',
    personality: saved?.personality || '',
    learningHabit: saved?.learningHabit || 'project',
    goal: saved?.goal || 'internship',
    level: saved?.level || 'beginner',
    dailyMinutes: Number(saved?.dailyMinutes) || 60,
    currentSkills: saved?.currentSkills || '',
    mbti: saved?.mbti || '',
  }
}

function hasUsableForm(form) {
  return Boolean(form.major || form.interest || form.personality || form.mbti)
}

function useOptions(t) {
  return useMemo(() => ({
    grades: [
      ['freshman', t('exploreFlow.gradeFreshman')],
      ['sophomore', t('exploreFlow.gradeSophomore')],
      ['junior', t('exploreFlow.gradeJunior')],
      ['senior', t('exploreFlow.gradeSenior')],
      ['graduate', t('exploreFlow.gradeGraduate')],
    ],
    habits: [
      ['project', t('exploreFlow.habitProject')],
      ['video', t('exploreFlow.habitVideo')],
      ['reading', t('exploreFlow.habitReading')],
      ['practice', t('exploreFlow.habitPractice')],
      ['deadline', t('exploreFlow.habitDeadline')],
    ],
    goals: [
      ['internship', t('exploreFlow.goalInternship')],
      ['job', t('exploreFlow.goalJob')],
      ['graduate', t('exploreFlow.goalGraduate')],
      ['portfolio', t('exploreFlow.goalPortfolio')],
      ['stable', t('exploreFlow.goalStable')],
      ['unsure', t('exploreFlow.goalUnsure')],
    ],
    levels: [
      ['beginner', t('exploreFlow.levelBeginner')],
      ['foundation', t('exploreFlow.levelFoundation')],
      ['intermediate', t('exploreFlow.levelIntermediate')],
      ['advanced', t('exploreFlow.levelAdvanced')],
    ],
    dailyMinutes: [15, 30, 45, 60, 90, 120],
  }), [t])
}

function parseCurrentSkills(value) {
  return String(value || '')
    .split(/[,，、;；\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 20)
}

function StepField({ step, form, onChange, t }) {
  const options = useOptions(t)
  if (step === 0) {
    return (
      <div className="career-explorer-step-fields">
        <label>
          <span>{t('exploreFlow.major')}</span>
          <input value={form.major} onChange={(event) => onChange('major', event.target.value)} placeholder={t('exploreFlow.majorPlaceholder')} />
        </label>
        <label>
          <span>{t('exploreFlow.grade')}</span>
          <select value={form.grade} onChange={(event) => onChange('grade', event.target.value)}>
            {options.grades.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>{t('exploreFlow.majorInterest')}</span>
          <select value={form.majorInterest} onChange={(event) => onChange('majorInterest', event.target.value)}>
            <option value="interested">{t('exploreFlow.majorInterestInterested')}</option>
            <option value="unsure">{t('exploreFlow.majorInterestUnsure')}</option>
            <option value="not_interested">{t('exploreFlow.majorInterestNotInterested')}</option>
          </select>
        </label>
        {form.majorInterest === 'not_interested' ? (
          <label>
            <span>{t('exploreFlow.preferredField')}</span>
            <input value={form.preferredField} onChange={(event) => onChange('preferredField', event.target.value)} placeholder={t('exploreFlow.preferredFieldPlaceholder')} />
            <small>{t('exploreFlow.preferredFieldNote')}</small>
          </label>
        ) : null}
      </div>
    )
  }

  if (step === 1) {
    return (
      <div className="career-explorer-step-fields">
        <label>
          <span>{t('exploreFlow.interest')}</span>
          <input value={form.interest} onChange={(event) => onChange('interest', event.target.value)} placeholder={t('exploreFlow.interestPlaceholder')} />
        </label>
      </div>
    )
  }

  if (step === 2) {
    return (
      <div className="career-explorer-step-fields">
        <label>
          <span>{t('exploreFlow.personality')}</span>
          <input value={form.personality} onChange={(event) => onChange('personality', event.target.value)} placeholder={t('exploreFlow.personalityPlaceholder')} />
        </label>
        <label>
          <span>{t('exploreFlow.mbti')}</span>
          <input value={form.mbti} onChange={(event) => onChange('mbti', event.target.value)} placeholder={t('exploreFlow.mbtiPlaceholder')} />
          <small>{t('exploreFlow.mbtiNote')}</small>
        </label>
      </div>
    )
  }

  if (step === 3) {
    return (
      <div className="career-explorer-step-fields">
        <label>
          <span>{t('exploreFlow.learningHabit')}</span>
          <select value={form.learningHabit} onChange={(event) => onChange('learningHabit', event.target.value)}>
            {options.habits.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>{t('exploreFlow.level')}</span>
          <select value={form.level} onChange={(event) => onChange('level', event.target.value)}>
            {options.levels.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>{t('exploreFlow.currentSkills')}</span>
          <input value={form.currentSkills} onChange={(event) => onChange('currentSkills', event.target.value)} placeholder={t('exploreFlow.currentSkillsPlaceholder')} />
          <small>{t('exploreFlow.currentSkillsNote')}</small>
        </label>
      </div>
    )
  }

  return (
    <div className="career-explorer-step-fields">
      <label>
        <span>{t('exploreFlow.goal')}</span>
        <select value={form.goal} onChange={(event) => onChange('goal', event.target.value)}>
          {options.goals.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select>
      </label>
      <label>
        <span>{t('exploreFlow.dailyMinutes')}</span>
        <select value={form.dailyMinutes} onChange={(event) => onChange('dailyMinutes', Number(event.target.value))}>
          {options.dailyMinutes.map((minutes) => <option value={minutes} key={minutes}>{t('exploreFlow.dailyMinutesOption', { minutes })}</option>)}
        </select>
        <small>{t('exploreFlow.dailyMinutesNote')}</small>
      </label>
    </div>
  )
}

function SelfDiscoveryForm({ form, onChange, onComplete, t }) {
  const [step, setStep] = useState(0)
  const steps = [
    { title: t('exploreFlow.stepMajorTitle'), desc: t('exploreFlow.stepMajorDesc') },
    { title: t('exploreFlow.stepInterestTitle'), desc: t('exploreFlow.stepInterestDesc') },
    { title: t('exploreFlow.stepPersonalityTitle'), desc: t('exploreFlow.stepPersonalityDesc') },
    { title: t('exploreFlow.stepHabitTitle'), desc: t('exploreFlow.stepHabitDesc') },
    { title: t('exploreFlow.stepGoalTitle'), desc: t('exploreFlow.stepGoalDesc') },
  ]
  const current = steps[step]
  const isFirst = step === 0
  const isLast = step === steps.length - 1
  const canContinue = step !== 0 || (Boolean(form.major.trim()) && (form.majorInterest !== 'not_interested' || Boolean(form.preferredField.trim())))

  function goNext() {
    if (isLast) {
      onComplete()
      return
    }
    setStep((value) => Math.min(value + 1, steps.length - 1))
  }

  return (
    <section className="career-explorer-step-card" aria-label={t('exploreFlow.selfSection')}>
      <div className="career-explorer-step-progress">
        <span>{t('exploreFlow.stepProgress', { current: step + 1, total: steps.length })}</span>
        <div aria-hidden="true">
          {steps.map((item, index) => (
            <i className={index <= step ? 'is-active' : ''} key={item.title} />
          ))}
        </div>
      </div>
      <header>
        <span>{t('exploreFlow.selfSection')}</span>
        <h2>{current.title}</h2>
        <p>{current.desc}</p>
      </header>
      <StepField step={step} form={form} onChange={onChange} t={t} />
      <footer>
        <button className="secondary-button" type="button" onClick={() => setStep((value) => Math.max(value - 1, 0))} disabled={isFirst}>
          <ArrowLeft size={16} /> {t('exploreFlow.previousStep')}
        </button>
        <button className="primary-button" type="button" onClick={goNext} disabled={!canContinue}>
          {isLast ? t('exploreFlow.generatePortrait') : t('exploreFlow.nextStepButton')} <ArrowRight size={16} />
        </button>
      </footer>
    </section>
  )
}

function DirectionList({ directions, selectedId, setSelectedId, t }) {
  return (
    <section className="career-explorer-results" aria-label={t('exploreFlow.recommendedDirections')}>
      <div className="career-explorer-direction-list">
        {directions.map((direction) => {
          const selected = selectedId === direction.key
          return (
            <button
              type="button"
              className={`career-explorer-card ${selected ? 'is-active' : ''}`}
              onClick={() => setSelectedId(direction.key)}
              key={direction.key}
            >
              <span><Compass size={16} /> {direction.subcategory || direction.category}</span>
              <strong>{selected ? t('exploreFlow.selected') : t('exploreFlow.reviewDirection')}</strong>
              <h3>{direction.name}</h3>
              <p>{direction.aiReason || direction.summary || direction.suitableFor}</p>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function DetailPicker({ direction, selectedDetailId, setSelectedDetailId }) {
  const items = direction?.details || []
  if (!items.length) return null
  return (
    <section className="career-explorer-detail-picker" aria-label="选择具体目标">
      <div>
        <span>第三步</span>
        <h2>选择具体目标</h2>
        <p>成长画像、学习路线和今日任务会围绕你选中的岗位展开。</p>
      </div>
      <div className="career-explorer-detail-options">
        {items.map((detail) => (
          <button
            type="button"
            className={selectedDetailId === detail.key ? 'is-active' : ''}
            onClick={() => setSelectedDetailId(detail.key)}
            key={detail.key}
          >
            <strong>{detail.name}</strong>
            <span>{detail.description}</span>
          </button>
        ))}
      </div>
    </section>
  )
}

function DirectionPortrait({ direction, detail, saving, generationMessage, generationElapsed, onCreateGoal, t }) {
  if (!direction) return null
  const target = detail?.name || direction.name
  const recommendation = direction.aiRecommendation
  const strengths = recommendation?.strengths?.length
    ? recommendation.strengths
    : (detail?.skills?.length ? detail.skills : direction.skills) || []
  const gaps = recommendation?.gaps?.length
    ? recommendation.gaps
    : direction.stages?.map((stage) => stage.stage).filter(Boolean) || []
  const firstStage = direction.stages?.[0]
  const nextAction = recommendation?.gaps?.[0]
    ? t('exploreFlow.closeFirstGap', { gap: recommendation.gaps[0] })
    : (firstStage ? `${firstStage.stage}：${firstStage.items?.slice(0, 3).join('、')}` : t('exploreFlow.auditBasics'))
  const outcome = direction.projects?.[0] || detail?.jobs?.[0] || direction.jobs?.[0] || target
  return (
    <section className="career-explorer-detail" id="career-explorer-selected-direction">
      <div>
        <span><GraduationCap size={18} /> {t('exploreFlow.directionProfile')}</span>
        <h2>{target}</h2>
        <small>{direction.category} / {direction.subcategory}</small>
        <p>{direction.summary}</p>
      </div>
      <div className="career-explorer-detail-grid">
        <article>
          <h3>{t('exploreFlow.whyFit')}</h3>
          <p>{direction.aiReason || direction.suitableFor}</p>
        </article>
        <article>
          <h3>{t('exploreFlow.currentStrengths')}</h3>
          <p>{strengths.slice(0, 5).join('、') || direction.category}</p>
        </article>
        <article>
          <h3>{t('exploreFlow.currentGaps')}</h3>
          <p>{gaps.slice(0, 5).join('、') || t('exploreFlow.noGapData')}</p>
        </article>
        <article>
          <h3>{t('exploreFlow.nextAction')}</h3>
          <p>{nextAction}</p>
        </article>
        <article className="is-wide">
          <h3>{t('exploreFlow.possibleOutcome')}</h3>
          <p>{(detail?.jobs?.length ? detail.jobs : direction.jobs)?.join('、')}</p>
        </article>
      </div>
      <aside className="career-explorer-companion">
        <span><Sparkles size={16} /> {t('exploreFlow.companionObservation')}</span>
        <p>{direction.aiReason || t('exploreFlow.companionDirectionText', { category: direction.category, direction: direction.name })}</p>
        <strong>{t('exploreFlow.companionAdvice')}</strong>
        <p>建议先完成：{nextAction}。目标成果可以先做成「{outcome}」。</p>
      </aside>
      <button className="primary-button" type="button" onClick={() => onCreateGoal(direction, detail)} disabled={saving}>
        {saving ? t('exploreFlow.generatingGoal') : t('exploreFlow.createGrowthGoal')} <ArrowRight size={16} />
      </button>
      {saving ? (
        <div className="career-explorer-generation-status" aria-live="polite">
          <strong>{generationMessage || '正在提交生成任务…'}</strong>
          <span>已等待 {generationElapsed} 秒。首次生成需要读取画像和知识库；相同画像下次会直接复用。</span>
        </div>
      ) : null}
    </section>
  )
}

function CareerPlannerPage({ mode = 'home' }) {
  const auth = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const hasPrefilledDirection = Boolean(searchParams.get('major') || searchParams.get('direction'))
  const [form, setForm] = useState(() => createInitialForm(searchParams))
  const [selectedId, setSelectedId] = useState('')
  const [selectedDetailId, setSelectedDetailId] = useState('')
  const [directionTemplates, setDirectionTemplates] = useState([])
  const [aiRecommendations, setAiRecommendations] = useState([])
  const [aiProfileAnalysis, setAiProfileAnalysis] = useState(null)
  const [aiRecommendationModel, setAiRecommendationModel] = useState('')
  const [aiMatchedMajor, setAiMatchedMajor] = useState(null)
  const [recommendationStatus, setRecommendationStatus] = useState('idle')
  const [recommendationRetry, setRecommendationRetry] = useState(0)
  const recommendationRequestRef = useRef('')
  const [hasExplored, setHasExplored] = useState(mode === 'results' && (hasPrefilledDirection || hasUsableForm(createInitialForm(searchParams))))
  const [saving, setSaving] = useState(false)
  const [generationElapsed, setGenerationElapsed] = useState(0)
  const generationStartedAtRef = useRef(0)
  const [message, setMessage] = useState('')
  const search = location.search || ''
  const formPath = `/growth/explore/form${search}`
  const resultsPath = `/growth/explore/results${search}`

  useEffect(() => {
    if (auth?.loading || !auth?.user || !saving) return undefined
    let timer; let cancelled = false
    const startedAt = generationStartedAtRef.current || Date.now()
    const poll = async () => {
      setGenerationElapsed(Math.max(0, Math.round((Date.now() - startedAt) / 1000)))
      try {
        const result = await getLatestAiGrowthPlanGeneration()
        const generation = result?.generation
        const status = generation?.status
        const generationTime = generation?.createdAt ? new Date(generation.createdAt).getTime() : 0
        const belongsToCurrentRequest = generationTime >= startedAt - 3000
        if (belongsToCurrentRequest && (status === 'generating' || status === 'validating')) {
          setSaving(true)
          setMessage(status === 'validating' ? 'AI 已生成初稿，正在检查专业性和可执行性…' : 'AI 正在结合你的画像和内部知识库生成专属路线…')
          timer = window.setTimeout(poll, 1800)
        } else if (belongsToCurrentRequest && status === 'completed') {
          navigate('/growth')
        } else if (belongsToCurrentRequest && status === 'failed') {
          setSaving(false)
          setMessage(result?.generation?.errorMessage || '专属路线生成失败，请重新生成。')
        } else {
          timer = window.setTimeout(poll, 1200)
        }
      } catch {
        if (!cancelled) timer = window.setTimeout(poll, 2500)
      }
    }
    poll()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [auth?.loading, auth?.user, navigate, saving])

  useEffect(() => {
    try {
      sessionStorage.setItem(EXPLORE_FORM_STORAGE_KEY, JSON.stringify(form))
    } catch {
      // Session storage is only a convenience for moving between the split explore steps.
    }
  }, [form])

  useEffect(() => {
    const controller = new AbortController()
    getCareerPlannerDirectionCatalog(controller.signal)
      .then((catalog) => {
        setDirectionTemplates(catalog.directions)
        setMessage('')
      })
      .catch((error) => {
        setMessage(error?.message || '方向模板加载失败，请稍后重试')
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (mode !== 'results' || !hasExplored) return undefined
    const requestKey = JSON.stringify(form)
    if (recommendationRequestRef.current === requestKey) return undefined
    recommendationRequestRef.current = requestKey
    const controller = new AbortController()
    setRecommendationStatus('loading')
    recommendAiGrowthCareers({
      major: form.major,
      majorInterest: form.majorInterest,
      preferredField: form.preferredField,
      grade: form.grade,
      interest: form.interest,
      personality: form.personality,
      learningHabit: form.learningHabit,
      goal: form.goal,
      currentSkills: form.currentSkills,
    }, controller.signal)
      .then((result) => {
        setAiRecommendations(Array.isArray(result?.recommendations) ? result.recommendations : [])
        setAiProfileAnalysis(result?.profileAnalysis || null)
        setAiRecommendationModel(result?.model || 'DeepSeek')
        setAiMatchedMajor(result?.major || null)
        setRecommendationStatus(result?.recommendations?.length ? 'success' : 'error')
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
        setAiRecommendations([])
        setAiProfileAnalysis(null)
        setRecommendationStatus('error')
        setMessage(error?.message || 'AI 方向画像生成失败，请补充信息后重试。')
      })
    return () => controller.abort()
  }, [form, hasExplored, mode, recommendationRetry])

  const aiDirections = useMemo(() => aiRecommendations.map((recommendation) => {
    const direction = directionTemplates.find((item) => (
      Number(item.id) === Number(recommendation.careerId) || item.key === recommendation.careerKey
    ))
    return direction ? { ...direction, aiReason: recommendation.reason, aiRecommendation: recommendation } : null
  }).filter(Boolean), [aiRecommendations, directionTemplates])
  const displayedDirections = recommendationStatus === 'success' && aiDirections.length ? aiDirections.slice(0, 5) : []

  const selected = hasExplored
    ? displayedDirections.find((direction) => direction.key === selectedId) || displayedDirections[0]
    : null
  const selectedDetail = selected?.details?.find((detail) => detail.key === selectedDetailId) || selected?.details?.[0] || null

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setMessage('')
  }

  function generatePortrait() {
    setHasExplored(true)
    setSelectedId('')
    setSelectedDetailId('')
    setMessage('')
    navigate(resultsPath)
  }

  async function createGoal(direction, detail) {
    generationStartedAtRef.current = Date.now()
    setGenerationElapsed(0)
    setSaving(true)
    setMessage('正在提交生成任务…')
    const target = detail?.name || direction.name
    let backgroundGeneration = false
    try {
      const result = await generateAiGrowthPlan({
        majorId: aiMatchedMajor?.id || null,
        careerId: direction.id || direction.aiRecommendation?.careerId || null,
        careerKey: direction.key,
        direction: target,
        grade: form.grade,
        level: form.level,
        goal: form.goal,
        dailyMinutes: Number(form.dailyMinutes) || 60,
        interest: [form.interest, form.personality].filter(Boolean),
        currentSkills: parseCurrentSkills(form.currentSkills),
        majorInterest: form.majorInterest,
        preferredField: form.preferredField,
      })
      backgroundGeneration = Boolean(result?.accepted)
      if (backgroundGeneration) {
        setMessage('生成任务已提交，你可以停留在这里查看进度，完成后会自动进入成长中心。')
      } else {
        navigate('/growth')
      }
    } catch (error) {
      if (error?.code === 'AI_GROWTH_PLAN_GENERATION_IN_PROGRESS') {
        backgroundGeneration = true
        setMessage('你的专属路线正在后台生成，完成后会自动进入成长中心。')
      } else {
        setMessage(error?.message || t('exploreFlow.createGoalFailed'))
      }
    } finally {
      if (!backgroundGeneration) setSaving(false)
    }
  }

  return (
    <main className="page-container career-explorer-page">
      <div className="student-project-rec-topbar">
        <Link className="secondary-button" to="/growth"><ArrowLeft size={16} /> {t('exploreFlow.backGrowth')}</Link>
      </div>

      {mode === 'home' ? (
        <>
          <section className="career-explorer-hero">
            <div>
              <span><Compass size={18} /> {t('exploreFlow.eyebrow')}</span>
              <h1>{t('exploreFlow.title')}</h1>
              <p>{t('exploreFlow.subtitle')}</p>
              <div className="career-explorer-hero-actions">
                <Link className="primary-button" to={formPath}>{t('exploreFlow.startExplore')} <ArrowRight size={16} /></Link>
                <Link className="secondary-button" to="/growth">{t('exploreFlow.backGrowth')}</Link>
              </div>
            </div>
          </section>

          <section className="ai-growth-journey" aria-label={t('exploreFlow.flowAria')}>
            <div className="ai-growth-journey-grid">
              {['knowSelf', 'aiExplore', 'profile', 'choose', 'goal', 'growth'].map((key, index) => (
                <article key={key}>
                  <span>{index + 1}</span>
                  <strong>{t(`exploreFlow.steps.${key}`)}</strong>
                </article>
              ))}
            </div>
          </section>
        </>
      ) : null}

      {mode === 'form' ? (
        <SelfDiscoveryForm form={form} onChange={updateForm} onComplete={generatePortrait} t={t} />
      ) : null}

      {mode === 'results' && hasExplored ? (
        <>
          <section className="career-explorer-section-head">
            <span>{t('exploreFlow.directionPortrait')}</span>
            <h2>{t('exploreFlow.resultsTitle')}</h2>
            <p>{t('exploreFlow.resultsDesc')}</p>
            <Link className="secondary-button" to={formPath}>{t('exploreFlow.editProfile')}</Link>
          </section>
          {recommendationStatus === 'loading' ? (
            <section className="career-explorer-ai-loading" aria-live="polite">
              <Sparkles size={20} />
              <div>
                <h2>{t('exploreFlow.aiAnalyzingTitle')}</h2>
                <p>{t('exploreFlow.aiAnalyzingDesc')}</p>
              </div>
            </section>
          ) : recommendationStatus === 'error' ? (
            <section className="student-growth-error">
              <strong>AI 方向画像暂未生成</strong>
              <p>{message || '没有使用固定岗位替代，请补充兴趣、能力或目标后重新分析。'}</p>
              <div className="career-explorer-hero-actions">
                <button className="primary-button" type="button" onClick={() => { recommendationRequestRef.current = ''; setMessage(''); setRecommendationRetry((value) => value + 1) }}>
                  重新调用 AI 分析
                </button>
                <Link className="secondary-button" to={formPath}>补充画像信息</Link>
              </div>
            </section>
          ) : displayedDirections.length ? (
            <>
              {aiProfileAnalysis ? (
                <section className="career-explorer-ai-profile" aria-label="AI 画像分析">
                  <div className="career-explorer-ai-profile-head">
                    <span><Sparkles size={17} /> AI 画像分析</span>
                    <small>{aiRecommendationModel} · 动态推荐</small>
                  </div>
                  <p>{aiProfileAnalysis.summary}</p>
                  <div className="career-explorer-ai-profile-grid">
                    <div><strong>你的可迁移优势</strong><p>{aiProfileAnalysis.strengths?.join('、') || '等待更多信息'}</p></div>
                    <div><strong>当前限制</strong><p>{aiProfileAnalysis.constraints?.join('、') || '暂未发现明确限制'}</p></div>
                    <div><strong>推荐依据</strong><p>{aiProfileAnalysis.recommendationLogic || '结合专业、兴趣、目标和岗位库生成。'}</p></div>
                    {aiProfileAnalysis.missingInformation?.length ? <div><strong>还可以补充</strong><p>{aiProfileAnalysis.missingInformation.join('、')}</p></div> : null}
                  </div>
                </section>
              ) : null}
              <div className="career-explorer-workspace">
                <DirectionList
                  directions={displayedDirections}
                  selectedId={selected?.key}
                  setSelectedId={(key) => {
                    setSelectedId(key)
                    setSelectedDetailId('')
                  }}
                  t={t}
                />
                <div className="career-explorer-workspace-detail">
                  <DetailPicker direction={selected} selectedDetailId={selectedDetail?.key} setSelectedDetailId={setSelectedDetailId} />
                  <DirectionPortrait direction={selected} detail={selectedDetail} onCreateGoal={createGoal} saving={saving} generationMessage={message} generationElapsed={generationElapsed} t={t} />
                </div>
              </div>
              <div className="career-explorer-mobile-dock" aria-label={t('exploreFlow.selectedDirection')}>
                <div>
                  <span>{t('exploreFlow.selectedDirection')}</span>
                  <strong>{selectedDetail?.name || selected?.name}</strong>
                </div>
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => document.getElementById('career-explorer-selected-direction')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  {t('exploreFlow.reviewSelectedDirection')} <ArrowRight size={16} />
                </button>
              </div>
            </>
          ) : (
            <section className="career-explorer-empty">
              <div>
                <span><Sparkles size={16} /> {t('exploreFlow.directionPortrait')}</span>
                <h2>方向模板加载中</h2>
                <p>系统正在读取职业方向模板，请稍后再试。</p>
              </div>
            </section>
          )}
        </>
      ) : null}

      {mode === 'results' && !hasExplored ? (
        <section className="career-explorer-empty">
          <div>
            <span><Sparkles size={16} /> {t('exploreFlow.selfSection')}</span>
            <h2>{t('exploreFlow.needFormTitle')}</h2>
            <p>{t('exploreFlow.needFormDesc')}</p>
          </div>
          <Link className="primary-button" to={formPath}>{t('exploreFlow.startExplore')} <ArrowRight size={16} /></Link>
        </section>
      ) : null}

      {message ? <p className="career-planner-save-error">{message}</p> : null}

      {mode === 'home' ? <section className="career-explorer-favorites">
        <h2><BookOpenCheck size={18} />{t('exploreFlow.existingLogic')}</h2>
        <p>{t('exploreFlow.existingLogicDesc')}</p>
        <CheckCircle2 size={1} aria-hidden="true" />
      </section> : null}
    </main>
  )
}

export default CareerPlannerPage
